import { useState } from 'react';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  IconButton,
  Tooltip,
  Button,
  HStack,
  Collapse,
  Input,
  Stack,
  Alert,
  AlertIcon,
  useColorMode,
  Spinner,
} from '@chakra-ui/react';
import {
  SearchIcon,
  SmallCloseIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';
import Card from 'components/card/Card';
import { apiGetDeferredDocuments } from 'api';
import { useViewAs } from 'hooks/useViewAs';
import { PolicyWithDeferredDocs } from 'types/documents';
import PolicyCard from './components/PolicyCard';

const DocumentsHubView = () => {
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [expandedPolicies, setExpandedPolicies] = useState<Set<string>>(
    new Set(),
  );
  const [clientNameFilter, setClientNameFilter] = useState('');
  const [binderNumberFilter, setBinderNumberFilter] = useState('');
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const { colorMode } = useColorMode();

  const colors = {
    textColor: colorMode === 'light' ? 'secondaryGray.900' : 'white',
    borderColor: colorMode === 'light' ? 'gray.200' : 'whiteAlpha.100',
    hoverBg: colorMode === 'light' ? 'gray.200' : 'whiteAlpha.100',
  } as const;

  const textColor = colors.textColor;
  const borderColor = colors.borderColor;

  const viewAs = useViewAs();

  const { data, isLoading, error } = useQuery({
    queryKey: ['deferredDocuments', viewAs.filterParams],
    queryFn: () => apiGetDeferredDocuments(viewAs.viewAsEmail, viewAs.viewAsBranchId),
    refetchInterval: 30000,
  });

  const policies: PolicyWithDeferredDocs[] = data?.policies || [];

  const togglePolicy = (uuid: string) => {
    setExpandedPolicies((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  // Filter and sort policies
  const filteredPolicies = React.useMemo(() => {
    let filtered = [...policies];

    // Apply filters
    if (clientNameFilter.trim()) {
      filtered = filtered.filter((p) =>
        p.client_name
          ?.toLowerCase()
          .includes(clientNameFilter.toLowerCase().trim()),
      );
    }
    if (binderNumberFilter.trim()) {
      filtered = filtered.filter((p) =>
        p.binder_number
          ?.toLowerCase()
          .includes(binderNumberFilter.toLowerCase().trim()),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortField === 'client_name') {
        aValue = a.client_name || '';
        bValue = b.client_name || '';
      } else if (sortField === 'binder_number') {
        aValue = a.binder_number || '';
        bValue = b.binder_number || '';
      } else if (sortField === 'created_at') {
        aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
        bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
      } else if (sortField === 'document_count') {
        aValue = a.deferred_documents.length;
        bValue = b.deferred_documents.length;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [
    policies,
    clientNameFilter,
    binderNumberFilter,
    sortField,
    sortDirection,
  ]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const resetSearch = () => {
    setClientNameFilter('');
    setBinderNumberFilter('');
    setSortField('created_at');
    setSortDirection('desc');
  };

  if (error) {
    return (
      <Card>
        <Alert status="error">
          <AlertIcon />
          Failed to load deferred documents. Please try again.
        </Alert>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Deferred Documents
        </Text>
        <HStack spacing={4}>
          <Tooltip
            label={showSearchBar ? 'Hide search' : 'Show search'}
            hasArrow
          >
            <IconButton
              aria-label={showSearchBar ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              variant={showSearchBar ? 'solid' : 'ghost'}
              colorScheme={showSearchBar ? 'yellow' : 'gray'}
              onClick={() => setShowSearchBar(!showSearchBar)}
              size="md"
            />
          </Tooltip>
        </HStack>
      </Flex>

      <Collapse in={showSearchBar} animateOpacity>
        <Box px="20px" mb="20px">
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box flex="1">
              <Input
                variant="main"
                placeholder="Search by client name"
                value={clientNameFilter}
                onChange={(e) => setClientNameFilter(e.target.value)}
                size="md"
              />
            </Box>
            <Box flex="1">
              <Input
                variant="main"
                placeholder="Search by binder number"
                value={binderNumberFilter}
                onChange={(e) => setBinderNumberFilter(e.target.value)}
                size="md"
              />
            </Box>
            <Flex gap={2}>
              <Button
                onClick={resetSearch}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
              >
                Reset
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Collapse>

      <Box overflowX="auto">
        <Table variant="simple" color="gray.500">
          <Thead>
            <Tr>
              <Th borderColor={borderColor} color="gray.400" w="50px">
                {/* Expand/Collapse column */}
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Client Name</Text>
                  <Tooltip label="Sort by Client Name">
                    <IconButton
                      aria-label="Sort by Client Name"
                      icon={
                        sortField === 'client_name' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('client_name')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Binder Number</Text>
                  <Tooltip label="Sort by Binder Number">
                    <IconButton
                      aria-label="Sort by Binder Number"
                      icon={
                        sortField === 'binder_number' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('binder_number')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Created Date</Text>
                  <Tooltip label="Sort by Created Date">
                    <IconButton
                      aria-label="Sort by Created Date"
                      icon={
                        sortField === 'created_at' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('created_at')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Pending Documents</Text>
                  <Tooltip label="Sort by Document Count">
                    <IconButton
                      aria-label="Sort by Document Count"
                      icon={
                        sortField === 'document_count' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('document_count')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400" w="80px">
                <Text color="gray.400">Actions</Text>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  <Flex align="center" justify="center" mt="20px">
                    <Spinner size="sm" mr="10px" /> Loading documents...
                  </Flex>
                </Td>
              </Tr>
            )}

            {filteredPolicies.map((policy) => (
              <PolicyCard
                key={policy.uuid}
                policy={policy}
                isExpanded={expandedPolicies.has(policy.uuid)}
                onToggle={() => togglePolicy(policy.uuid)}
              />
            ))}
          </Tbody>
        </Table>
      </Box>

      {filteredPolicies.length === 0 && !isLoading && (
        <Box textAlign="center" w="100%" mt="20px" mb="20px">
          {clientNameFilter || binderNumberFilter
            ? 'No policies match your search criteria'
            : 'No deferred documents found'}
        </Box>
      )}
    </Card>
  );
};

export default DocumentsHubView;
